/** Demo Mode only - Finance > Forecast tab. Rolling forecast summary (Original budget /
 * Previous forecast / Current forecast / Actuals to date) plus a "Forecast drivers"
 * section explaining what moved the forecast since the previous version. */

import { useEffect } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../components/ui/Section';
import { applyRoundAndFormat } from '../../../utils';
import useCurrencyCodeConfiguration from '../../../hooks/configurations/useCurrencyCodeConfiguration';
import { computeTotals, sumLineItems, companyMonthlyTrend } from '../../../mocks/data/financeData';
import { useFinanceFilters } from '../FinanceFiltersContext';
import TrendChart from '../components/TrendChart';

const ForecastTab = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const { scopedContracts } = useFinanceFilters();

  useEffect(() => {
    getCurrencyCodeConfiguration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatCurrency = (value: number) => (value < 0 ? `(${applyRoundAndFormat(Math.abs(value), currencyCode)})` : applyRoundAndFormat(value, currencyCode));

  const budget = computeTotals(sumLineItems(scopedContracts.map((c) => c.scenarios.budget)));
  const previousForecast = computeTotals(sumLineItems(scopedContracts.map((c) => c.scenarios.previousForecast)));
  const currentForecast = computeTotals(sumLineItems(scopedContracts.map((c) => c.scenarios.currentForecast)));
  const actual = computeTotals(sumLineItems(scopedContracts.map((c) => c.scenarios.actual)));

  const rows = [
    { key: 'totalRevenue', label: t('financePnlTotalRevenue', 'Total revenue') },
    { key: 'foodAndBeverage', label: t('financeForecastFoodCost', 'Food cost'), invert: true },
    { key: 'totalLabour', label: t('financeForecastLabourCost', 'Labour cost'), invert: true },
    { key: 'totalOpex', label: t('financeForecastOtherOperatingCosts', 'Other operating costs'), invert: true },
    { key: 'grossProfit', label: t('financePnlGrossProfit', 'Gross profit') },
    { key: 'ebit', label: t('financePnlEbit', 'EBIT') },
    { key: 'ebitMarginPercentage', label: t('financePnlEbitMargin', 'EBIT margin'), isPercentage: true },
  ] as const;

  const valueFor = (totals: ReturnType<typeof computeTotals>, key: string, invert?: boolean) => {
    const raw = key === 'foodAndBeverage' ? totals.totalCostOfSales : (totals as unknown as Record<string, number>)[key];
    return invert ? -raw : raw;
  };

  const formatCell = (value: number, isPercentage?: boolean) => {
    if (isPercentage) return `${value.toFixed(1)}%`;
    return formatCurrency(value);
  };

  const trendForecast = companyMonthlyTrend('currentForecast');
  const trendPrevious = companyMonthlyTrend('priorYear');
  const trendBudget = companyMonthlyTrend('budget');

  const revenueVarianceVsBudget = currentForecast.totalRevenue - budget.totalRevenue;

  const drivers = [
    { key: 'coversTransactions', label: t('financeForecastDriverCovers', 'Covers/transactions'), movement: revenueVarianceVsBudget >= 0 ? t('financeForecastUp', 'Up on plan') : t('financeForecastDown', 'Down on plan') },
    { key: 'asp', label: t('financeForecastDriverAsp', 'Average spend per head (ASP)'), movement: t('financeForecastBroadlyStable', 'Broadly stable') },
    { key: 'managementFees', label: t('financeForecastDriverManagementFees', 'Management fees'), movement: t('financeForecastBroadlyStable', 'Broadly stable') },
    { key: 'clientSubsidies', label: t('financeForecastDriverSubsidies', 'Client subsidies'), movement: t('financeForecastBroadlyStable', 'Broadly stable') },
    { key: 'foodCostPercentage', label: t('financeForecastDriverFoodCostPct', 'Food cost %'), movement: currentForecast.grossProfitPercentage < budget.grossProfitPercentage ? t('financeForecastWorsened', 'Worsened vs budget') : t('financeForecastImproved', 'Improved vs budget') },
    { key: 'foodInflation', label: t('financeForecastDriverFoodInflation', 'Food inflation'), movement: t('financeForecastAdversePressure', 'Adverse pressure across most contracts') },
    { key: 'supplierPricing', label: t('financeForecastDriverSupplierPricing', 'Supplier price changes'), movement: t('financeForecastModestIncrease', 'Modest increases negotiated down where possible') },
    { key: 'labourHours', label: t('financeForecastDriverLabourHours', 'Labour hours'), movement: currentForecast.labourPercentage > budget.labourPercentage ? t('financeForecastUp', 'Up on plan') : t('financeForecastOnPlan', 'On plan') },
    { key: 'avgLabourRate', label: t('financeForecastDriverAvgLabourRate', 'Average labour rate'), movement: t('financeForecastUp', 'Up on plan') },
    { key: 'agencyLabour', label: t('financeForecastDriverAgencyLabour', 'Agency labour'), movement: currentForecast.agencyLabour > budget.agencyLabour ? t('financeForecastUp', 'Up on plan') : t('financeForecastOnPlan', 'On plan') },
    { key: 'hospitalityEvents', label: t('financeForecastDriverHospitality', 'Planned hospitality/events'), movement: t('financeForecastSeasonalSpike', 'Seasonal spike phased into H2') },
    { key: 'mobilisation', label: t('financeForecastDriverMobilisation', 'Mobilisation'), movement: t('financeForecastOneContractRampingUp', 'One contract still ramping up') },
    { key: 'commercialAdjustments', label: t('financeForecastDriverCommercialAdjustments', 'Known commercial adjustments'), movement: t('financeForecastNoneOutstanding', 'None outstanding') },
  ];

  return (
    <Box>
      <Section style={{ p: 4, mb: 6 }}>
        <TrendChart
          title={t('financeForecastTrendTitle', 'EBIT - current forecast vs budget vs prior year')}
          series={[
            { label: t('financePnlCurrentForecast', 'Current forecast'), data: trendForecast.ebit, color: '#1976d2' },
            { label: t('financePnlBudget', 'Budget'), data: trendBudget.ebit, color: theme.palette.custom.gray[400], dashed: true },
            { label: t('financePnlPriorYear', 'Prior year'), data: trendPrevious.ebit, color: '#9c27b0' },
          ]}
          valueFormatter={(v) => (v === null ? '' : applyRoundAndFormat(v, currencyCode))}
        />
      </Section>

      <Section style={{ p: 4, mb: 6 }}>
        <Typography variant="textMD"
          fontWeight={600}
          sx={{ mb: 3 }}>
          {t('financeForecastSummaryTitle', 'Rolling forecast summary (full year)')}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('financePnlLine', 'P&L line')}</TableCell>
                <TableCell align="right">{t('financeForecastOriginalBudget', 'Original budget')}</TableCell>
                <TableCell align="right">{t('financeForecastPreviousForecast', 'Previous forecast')}</TableCell>
                <TableCell align="right">{t('financePnlCurrentForecast', 'Current forecast')}</TableCell>
                <TableCell align="right">{t('financeForecastActualsToDate', 'Actuals to date')}</TableCell>
                <TableCell align="right">{t('financeForecastVarianceVsBudget', 'Full-year variance vs budget')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const budgetValue = valueFor(budget, r.key, 'invert' in r && r.invert);
                const currentValue = valueFor(currentForecast, r.key, 'invert' in r && r.invert);
                const variance = currentValue - budgetValue;
                return (
                  <TableRow key={r.key}>
                    <TableCell sx={{ fontWeight: r.key === 'ebit' || r.key === 'totalRevenue' ? 600 : 400 }}>{r.label}</TableCell>
                    <TableCell align="right">{formatCell(budgetValue, 'isPercentage' in r && r.isPercentage)}</TableCell>
                    <TableCell align="right">{formatCell(valueFor(previousForecast, r.key, 'invert' in r && r.invert), 'isPercentage' in r && r.isPercentage)}</TableCell>
                    <TableCell align="right"
                      sx={{ fontWeight: 600 }}>{formatCell(currentValue, 'isPercentage' in r && r.isPercentage)}</TableCell>
                    <TableCell align="right">{formatCell(valueFor(actual, r.key, 'invert' in r && r.invert), 'isPercentage' in r && r.isPercentage)}</TableCell>
                    <TableCell align="right"
                      sx={{ color: variance < 0 ? theme.palette.error.main : theme.palette.success.main }}>
                      {'isPercentage' in r && r.isPercentage
                        ? `${variance >= 0 ? '+' : '-'}${Math.abs(variance).toFixed(1)}pts`
                        : `${variance < 0 ? '(' : '+'}${applyRoundAndFormat(Math.abs(variance), currencyCode)}${variance < 0 ? ')' : ''}`}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>

      <Section style={{ p: 4 }}>
        <Typography variant="textMD"
          fontWeight={600}
          sx={{ mb: 3 }}>
          {t('financeForecastDriversTitle', 'Forecast drivers - what moved vs the previous forecast')}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('financeForecastDriver', 'Driver')}</TableCell>
                <TableCell>{t('financeForecastMovement', 'Movement since previous forecast')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {drivers.map((driver) => (
                <TableRow key={driver.key}>
                  <TableCell>{driver.label}</TableCell>
                  <TableCell>{driver.movement}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>
    </Box>
  );
};

export default ForecastTab;
